<script setup>
import { computed, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'

// 邮箱验证码登录：登录和注册同一条路，邮箱 → 发码 → 输码 → 进入
const props = defineProps({
  account: { type: Object, required: true },
  initialEmail: { type: String, default: '' },
})
const emit = defineEmits(['signed-in'])
const { t } = useI18n()

const email = ref(props.initialEmail)
const verificationCode = ref('')
const notice = ref('')
const localError = ref('')
const codeSent = ref(false)
const countdown = ref(0)
const busy = computed(() => props.account.authBusy.value)
let countdownTimer = null

onBeforeUnmount(() => {
  if (countdownTimer !== null) globalThis.clearInterval(countdownTimer)
})

function startCountdown() {
  countdown.value = 60
  countdownTimer = globalThis.setInterval(() => {
    countdown.value -= 1
    if (countdown.value > 0) return
    globalThis.clearInterval(countdownTimer)
    countdownTimer = null
  }, 1000)
}

function validateEmail() {
  if (!email.value) {
    localError.value = t('visualGame.account.emailRequired')
    return false
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value)) {
    localError.value = t('visualGame.account.emailInvalid')
    return false
  }
  return true
}

async function sendCode() {
  if (busy.value || countdown.value > 0) return
  localError.value = ''
  if (!validateEmail()) return
  try {
    await props.account.sendLoginCode({ email: email.value })
    codeSent.value = true
    notice.value = t('auth.register.codeSent')
    startCountdown()
  } catch {
    localError.value = props.account.error.value
  }
}

async function submit() {
  notice.value = ''
  localError.value = ''
  if (!validateEmail()) return
  if (!/^\d{6}$/.test(verificationCode.value)) {
    localError.value = t('visualGame.account.verificationCodePlaceholder')
    return
  }
  try {
    await props.account.loginWithEmailCode({ email: email.value, verification_code: verificationCode.value })
    emit('signed-in')
  } catch {
    localError.value = props.account.error.value
  }
}
</script>

<template>
  <form class="start-auth-form" novalidate @submit.prevent="submit">
    <p class="start-auth-form-hint">{{ t('auth.social.codeHint') }}</p>
    <label>
      <span>{{ t('visualGame.account.email') }}</span>
      <span class="auth-email-row">
        <input v-model.trim="email" type="email" autocomplete="email" :placeholder="t('visualGame.account.emailPlaceholder')" />
        <button type="button" :disabled="busy || countdown > 0" @click="sendCode">
          {{ countdown > 0 ? `${countdown}s` : codeSent ? t('visualGame.account.resendCode') : t('visualGame.account.sendCode') }}
        </button>
      </span>
    </label>
    <label>
      <span>{{ t('visualGame.account.verificationCode') }}</span>
      <input v-model.trim="verificationCode" inputmode="numeric" autocomplete="one-time-code" :placeholder="t('visualGame.account.verificationCodePlaceholder')" />
    </label>
    <p v-if="notice" class="account-notice">{{ notice }}</p>
    <p v-if="localError" class="account-error">{{ localError }}</p>
    <div class="auth-actions">
      <button class="auth-submit-button" type="submit" :disabled="busy">
        {{ busy ? t('visualGame.status.loading') : t('auth.social.continue') }}
      </button>
    </div>
  </form>
</template>
